import { api } from './client';

// Auth API
export const authApi = {
  login: (email: string, password: string, twoFactorCode?: string) =>
    api.post<{ token: string; user: import('../types').User }>('/auth/login', {
      email,
      password,
      two_factor_code: twoFactorCode,
    }),

  logout: (deviceToken?: string, authorizationToken?: string) =>
    api.post<{ message: string }>(
      '/auth/logout',
      deviceToken ? { device_token: deviceToken } : undefined,
      authorizationToken
        ? { headers: { Authorization: `Bearer ${authorizationToken}` } }
        : undefined,
    ),

  me: () => api.get<{ user: import('../types').User }>('/auth/me'),

  refresh: () => api.post<{ token: string }>('/auth/refresh'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ message: string }>('/auth/change-password', { currentPassword, newPassword }),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post<{ message: string }>('/auth/reset-password', { token, newPassword }),

  verifyResetToken: (token: string) =>
    api.get<{ valid: boolean; error?: string }>(`/auth/verify-reset-token/${token}`),

  // User invite flow
  verifyInviteToken: (token: string) =>
    api.get<{ valid: boolean; user?: { name: string; email: string }; error?: string }>(
      `/auth/verify-invite-token/${token}`,
    ),

  acceptInvite: (token: string, password: string) =>
    api.post<{ message: string; token: string; user: import('../types').User }>(
      '/auth/accept-invite',
      { token, password },
    ),

  resendInvite: (userId: string) => api.post<{ message: string }>(`/auth/resend-invite/${userId}`),

  // Session management
  getSessions: () =>
    api.get<{
      sessions: Array<{
        id: string;
        device: string;
        browser: string;
        location: string;
        lastActive: string;
        current: boolean;
      }>;
    }>('/auth/sessions'),

  revokeSession: (sessionId: string) =>
    api.delete<{ message: string }>(`/auth/sessions/${sessionId}`),

  revokeAllSessions: () => api.delete<{ message: string }>('/auth/sessions'),

  // Account management
  deleteAccount: (password: string, confirmation: string) =>
    api.delete<{ message: string; deletion_window_days: number }>('/auth/account', {
      password,
      confirmation,
    }),

  // Notification settings
  getNotificationSettings: (authorizationToken?: string) =>
    api.get<{
      email_notifications: Record<string, boolean>;
      browser_enabled: boolean;
    }>(
      '/auth/settings/notifications',
      undefined,
      authorizationToken
        ? { headers: { Authorization: `Bearer ${authorizationToken}` } }
        : undefined,
    ),

  updateNotificationSettings: (settings: {
    email_notifications: Record<string, boolean>;
    browser_enabled: boolean;
  }) => api.put<{ message: string }>('/auth/settings/notifications', settings),

  // Login history
  getLoginHistory: (params?: { limit?: number; offset?: number }) =>
    api.get<{
      history: Array<{
        id: string;
        status: 'success' | 'failed';
        device: string;
        browser: string;
        location: string;
        created_at: string;
      }>;
    }>('/auth/login-history', params),

  // Appearance settings
  getAppearanceSettings: () =>
    api.get<{
      theme: 'light';
      defaultView: import('../types').ViewMode;
      compactMode: boolean;
      showAvatars: boolean;
      timezone: string;
      dateFormat: string;
    }>('/auth/settings/appearance'),

  updateAppearanceSettings: (settings: {
    theme?: 'light';
    defaultView?: import('../types').ViewMode;
    compactMode?: boolean;
    showAvatars?: boolean;
    timezone?: string;
    dateFormat?: string;
  }) => api.put<{ message: string }>('/auth/settings/appearance', settings),

  // Two-factor authentication
  get2FAStatus: () => api.get<{ enabled: boolean }>('/auth/2fa/status'),

  setup2FA: () =>
    api.post<{ secret: string; qrCode: string; backupCodes: string[] }>('/auth/2fa/setup', {}),

  verify2FA: (code: string) =>
    api.post<{ message: string; backupCodes: string[] }>('/auth/2fa/verify', { code }),

  disable2FA: (code: string) => api.post<{ message: string }>('/auth/2fa/disable', { code }),

  /** @deprecated Use setup2FA and verify2FA instead */
  toggle2FA: (enabled: boolean) =>
    api.put<{ message: string; enabled: boolean }>('/auth/2fa/toggle', { enabled }),
};
